import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { fetchPausedFlags, PAUSED_FLAGS_KEY, type PausedFlag } from '@/features/system/api/system';
import { onFeatureDisabled } from '@/features/system/pausedFlags';

/** How often the dashboard asks. A switch flipped back on shows within this. */
const PAUSED_POLL_MS = 60_000;

/**
 * Which capabilities the platform has paused for this business.
 *
 * Fails open, like `useEntitlements`: until the answer arrives, or if it never
 * does, nothing is paused - a slow call must not make a merchant's tools look
 * broken, and the API still refuses what is really switched off.
 */
export function usePausedFlags() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: PAUSED_FLAGS_KEY,
    queryFn: fetchPausedFlags,
    staleTime: 30_000,
    refetchInterval: PAUSED_POLL_MS,
    retry: false,
  });

  // A refusal is fresher news than the poll: ask again now.
  useEffect(
    () =>
      onFeatureDisabled(() => {
        void queryClient.invalidateQueries({ queryKey: PAUSED_FLAGS_KEY });
      }),
    [queryClient],
  );

  const paused = (flag: string | undefined): PausedFlag | null => {
    if (!flag || !query.data) return null;
    return query.data.find((entry) => entry.key === flag) ?? null;
  };

  /** Every paused key, sorted and joined: a cheap dependency for memos. */
  const pausedKeys = (query.data ?? []).map((entry) => entry.key).sort().join(',');

  return { paused, pausedKeys, refetch: query.refetch, fetching: query.isFetching };
}
